/**
 * BatchGrantBonusDialog.tsx — 批量发放 BONUS（多选工具条，ADM-FIN-S004 配套）· 已接真
 *
 * 契约无批量端点 → 前端以最多 5 并发循环调既有单发 POST /api/v1/finance/grant-bonus。
 * 该端点无请求级重试幂等：仅 401/422 认定未写入，网络/5xx/未知错误一律提示人工对账。
 * money 路径限 root（前端 isRoot 守卫 + 后端 requireRole('root') 为权威）。
 *
 * body 严格按 shared GrantBonusRequest：{ user_id(UUID), amount(正整数 string ≤ 2000), reason_i18n }。
 * reason_i18n 必含非空 'en'（en = server 派生失败的 fallback 地板）。发放原因展示语言不再由运营手选：
 * 由 server 按各用户最近语言在 C 端展示侧派生（dev-docs A1 §9 / #167，PR #321 merged）。故移除原
 * 「按用户所属 App 解析 locale + 无 App 排除」整套；选中用户一律发放。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useSysConfig } from '~/data/queries';
import type { UserRow } from '~/data/queries';
import { financeApi } from '~/hooks/use-finance';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UiFormRow } from '~/ui/FormRow';
import { MultiLangFormRows, type MultiLangValue } from '~/ui/MultiLangFormRows';
import { toast } from '~/ui/Toast';
import { executeBatchBonusGrants } from './batch-grant-bonus-execution';

const FormRow = (p: FormRowProps) => (
  <UiFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

const PRESETS = [100, 500, 1000, 2000];
const REASONS = ['用户补偿', '客服补偿', '活动奖励', '故障补偿', 'VIP 关怀'];
const MAX_BATCH_BONUS_USERS = 50;
const UNAVAILABLE_DISPLAY_USER_ID = '不可用';

interface BatchGrantSummary {
  attempt: number;
  succeededCount: number;
  safeToRetryUsers: UserRow[];
  ambiguousCount: number;
}

interface ReconciliationErrorSnapshot {
  readonly requestId?: string;
  readonly code: string;
  readonly httpStatus: number;
}

interface ReconciliationUserSnapshot {
  readonly name: string;
  readonly displayUserId: string;
  readonly internalUserId: string;
  readonly error?: ReconciliationErrorSnapshot;
}

interface ReconciliationBatchRecord {
  readonly batchNumber: number;
  readonly attempt: number;
  readonly recordedAt: string;
  readonly amount: string;
  readonly reasonI18n: Readonly<Record<string, string>>;
  readonly users: readonly ReconciliationUserSnapshot[];
}

function snapshotApiError(error: unknown): ReconciliationErrorSnapshot | undefined {
  if (!(error instanceof AdminApiError)) return undefined;
  const hasRequestId = typeof error.requestId === 'string' && error.requestId.trim().length > 0;
  return Object.freeze({
    ...(hasRequestId ? { requestId: error.requestId } : {}),
    code: error.code,
    httpStatus: error.httpStatus,
  });
}

function serializeReconciliationLedger(records: readonly ReconciliationBatchRecord[]): string {
  return JSON.stringify(records, null, 2);
}

function getReconciliationDisplayUserId(displayUserId: string): string {
  return displayUserId.trim().length > 0 ? displayUserId : UNAVAILABLE_DISPLAY_USER_ID;
}

export function BatchGrantBonusDialog({
  users,
  onClose,
  onSelectionReconciled,
}: {
  users: UserRow[];
  onClose: () => void;
  /** 每批结算后只保留确定失败用户；成功与结果不明确用户均移出直接重试选择。 */
  onSelectionReconciled: (safeToRetryUserIds: string[]) => void;
}) {
  const { data: sysConfig } = useSysConfig();

  // 必填行 = zh/en；契约只强校验非空 'en'（reason 展示语言由 server 按各用户最近语言在 C 端派生）。
  // 必填只有 'en' —— 契约地板（`02-API契约/A1-运营财务.md` §9.1：reason_i18n 需 ≥1 键且
  // 必含非空 'en'，否则 FINANCE_BONUS_REASON_LOCALE_REQUIRED）。zh 仍固定展示并预填快捷原因，
  // 但**不强制**：发放时 server 按该 user 的 last_seen_language 从池里锁单语快照，而语言 Tab 候选集
  // 明文排除 zh（`01-通用约定/85-运营配置维度模型.md` §1.1）——zh 值永远不会被选成展示快照，
  // 强制它只是拦住「只填英文」这种完全合法的发放。
  const requiredLangs = ['en'];
  const pinnedLangs = ['zh', 'en'];
  const enabledLangs = sysConfig.languages.filter((l) => l.enabled).map((l) => l.language_code);

  const [amount, setAmount] = useState(500);
  const [reasonI18n, setReasonI18n] = useState<MultiLangValue>({ zh: REASONS[0] ?? '', en: '' });
  const [summary, setSummary] = useState<BatchGrantSummary | null>(null);
  const [reconciliationLedger, setReconciliationLedger] = useState<
    readonly ReconciliationBatchRecord[]
  >([]);
  const [reconciliationAcknowledged, setReconciliationAcknowledged] = useState(false);
  const attemptSequence = useRef(0);
  const reconciliationBatchSequence = useRef(0);
  const resultTitleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!summary) return;
    const resultTitle = resultTitleRef.current;
    if (!resultTitle) return;
    if (typeof resultTitle.scrollIntoView === 'function') {
      resultTitle.scrollIntoView({ block: 'start' });
    }
    resultTitle.focus();
  }, [summary]);

  const queryClient = useQueryClient();
  const grant = useMutation({
    mutationFn: async () => {
      const attempt = ++attemptSequence.current;
      const amountSnapshot = String(amount);
      const reasonI18nSnapshot = Object.freeze(
        Object.fromEntries(
          Object.entries(reasonI18n)
            .map(([k, v]) => [k, v.trim()] as const)
            .filter(([, v]) => v.length > 0),
        ),
      );
      const outcomes = await executeBatchBonusGrants(users, (user) =>
        financeApi.grantBonus({
          user_id: user.id,
          amount: amountSnapshot,
          reason_i18n: reasonI18nSnapshot,
        }),
      );
      const succeededUsers: UserRow[] = [];
      const safeToRetryUsers: UserRow[] = [];
      const ambiguousOutcomes: Array<{
        user: UserRow;
        error: unknown;
      }> = [];
      for (const outcome of outcomes) {
        if (outcome.status === 'succeeded') succeededUsers.push(outcome.recipient);
        else if (outcome.status === 'safe-to-retry') safeToRetryUsers.push(outcome.recipient);
        else ambiguousOutcomes.push({ user: outcome.recipient, error: outcome.error });
      }
      return {
        attempt,
        recordedAt: new Date().toISOString(),
        amountSnapshot,
        reasonI18nSnapshot,
        succeededUsers,
        safeToRetryUsers,
        ambiguousOutcomes,
      };
    },
    onSuccess: ({
      attempt,
      recordedAt,
      amountSnapshot,
      reasonI18nSnapshot,
      succeededUsers,
      safeToRetryUsers,
      ambiguousOutcomes,
    }) => {
      const reconciliationRecord =
        ambiguousOutcomes.length > 0
          ? Object.freeze({
              batchNumber: ++reconciliationBatchSequence.current,
              attempt,
              recordedAt,
              amount: amountSnapshot,
              reasonI18n: reasonI18nSnapshot,
              users: Object.freeze(
                ambiguousOutcomes.map(({ user, error }) => {
                  const errorSnapshot = snapshotApiError(error);
                  return Object.freeze({
                    name: user.name,
                    displayUserId: user.displayUserId,
                    internalUserId: user.id,
                    ...(errorSnapshot ? { error: errorSnapshot } : {}),
                  });
                }),
              ),
            })
          : null;
      const hasReconciliationLedger =
        reconciliationLedger.length > 0 || reconciliationRecord !== null;
      onSelectionReconciled(safeToRetryUsers.map((user) => user.id));
      queryClient.invalidateQueries({ queryKey: ['users'] });

      if (safeToRetryUsers.length === 0 && !hasReconciliationLedger) {
        toast(
          `已向 ${succeededUsers.length} 个用户发放 ${Number(amountSnapshot).toLocaleString()} BONUS`,
          {
            tone: 'pos',
            icon: 'IconCoin',
          },
        );
        onClose();
        return;
      }

      if (reconciliationRecord) {
        setReconciliationLedger((previous) => [...previous, reconciliationRecord]);
        setReconciliationAcknowledged(false);
      }
      setSummary({
        attempt,
        succeededCount: succeededUsers.length,
        safeToRetryUsers,
        ambiguousCount: ambiguousOutcomes.length,
      });
      toast('批量发放完成 · 请在弹窗核对结果与下一步', {
        tone: 'warn',
        icon: 'IconAlert',
      });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '批量发放编排失败，未自动重试', {
        tone: 'neg',
        icon: 'IconAlert',
      }),
  });

  const canSubmit =
    amount > 0 &&
    amount <= 2000 && // 全局上限 2000（dev-docs A1 §9 / #167）
    users.length > 0 &&
    users.length <= MAX_BATCH_BONUS_USERS &&
    // 必填 locale（en）有文案即可——与 MultiLangFormRows 标记的必填行一致
    requiredLangs.every((lang) => (reasonI18n[lang] ?? '').trim().length > 0) &&
    !grant.isPending;

  const requestClose = () => {
    if (grant.isPending) {
      toast('发放进行中，请等待批次完成后再关闭', {
        tone: 'warn',
        icon: 'IconAlert',
      });
      return;
    }
    if (reconciliationLedger.length > 0 && !reconciliationAcknowledged) {
      toast('请先确认已记录并将人工对账，再关闭弹窗', {
        tone: 'warn',
        icon: 'IconAlert',
      });
      return;
    }
    onClose();
  };

  const copyAllReconciliationRecords = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(serializeReconciliationLedger(reconciliationLedger));
      toast('已复制全部对账记录', {
        tone: 'pos',
        icon: 'IconCopy',
      });
    } catch {
      toast('复制全部对账记录失败', {
        tone: 'neg',
        icon: 'IconAlert',
      });
    }
  };

  const downloadReconciliationLedgerAsJson = () => {
    const blob = new Blob([serializeReconciliationLedger(reconciliationLedger)], {
      type: 'application/json;charset=utf-8',
    });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = 'batch-bonus-reconciliation.json';
    document.body.appendChild(anchor);
    try {
      anchor.click();
    } finally {
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    }
  };

  return (
    <Dialog
      width={480}
      title="批量发放 BONUS"
      subtitle={`已选 ${users.length} 个用户`}
      icon="IconCoin"
      onClose={requestClose}
      footer={
        <>
          <span style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            逐用户发放 · 并发最多 5 · 立即到账 · 每条记入审计日志
          </span>
          <button className="btn" onClick={requestClose}>
            取消
          </button>
          <button className="btn btn-accent" disabled={!canSubmit} onClick={() => grant.mutate()}>
            <Icons.IconCoin size={11} />
            {grant.isPending ? '发放中…' : `确认发放（${users.length}）`}
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        {summary && (
          <div
            className="card-flat"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={{ padding: 12, marginBottom: 12, fontSize: 11.5 }}
          >
            <div ref={resultTitleRef} tabIndex={-1} style={{ fontWeight: 600, color: 'var(--fg)' }}>
              当前尝试 · 第 {summary.attempt} 次
            </div>
            <div style={{ marginTop: 4 }}>
              成功 {summary.succeededCount} · 可安全重试 {summary.safeToRetryUsers.length} ·
              结果不明确 {summary.ambiguousCount}
            </div>
            {summary.safeToRetryUsers.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ color: 'var(--warn)', fontWeight: 600 }}>
                  以下用户确定未写入，可安全重试：
                </div>
                <div className="vstack" style={{ gap: 5, marginTop: 6 }}>
                  {summary.safeToRetryUsers.map((user) => (
                    <div key={user.id}>
                      <div>{user.name}</div>
                      <div className="mono" style={{ color: 'var(--fg-muted)' }}>
                        展示用户 ID · {getReconciliationDisplayUserId(user.displayUserId)}
                      </div>
                      <div className="mono" style={{ color: 'var(--fg-muted)' }}>
                        内部 UUID · {user.id}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {summary.ambiguousCount > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ color: 'var(--neg)', fontWeight: 600 }}>
                  本次结果不明确用户已追加到下方待人工对账记录。
                </div>
              </div>
            )}
          </div>
        )}
        {reconciliationLedger.length > 0 && (
          <div className="card-flat" style={{ padding: 12, marginBottom: 12, fontSize: 11.5 }}>
            <div style={{ fontWeight: 600, color: 'var(--neg)' }}>
              待人工对账记录 · {reconciliationLedger.length} 批
            </div>
            <div style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
              请求结果可能已经到账，不可直接重试。请先人工对账（核查钱包余额、BONUS
              流水与审计日志），确认未到账后再决定是否重新发放。
            </div>
            <div className="hstack" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-sm" onClick={copyAllReconciliationRecords}>
                <Icons.IconCopy size={11} />
                复制全部记录
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={downloadReconciliationLedgerAsJson}
              >
                <Icons.IconDownload size={11} />
                下载 JSON
              </button>
            </div>
            <div className="vstack" style={{ gap: 12, marginTop: 10 }}>
              {reconciliationLedger.map((record) => (
                <div
                  key={record.batchNumber}
                  style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}
                >
                  <div style={{ fontWeight: 600 }}>
                    对账批次 #{record.batchNumber} · 第 {record.attempt} 次尝试
                  </div>
                  <div className="mono" style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
                    记录时间 · {record.recordedAt}
                  </div>
                  <div style={{ marginTop: 4 }}>数额 · {record.amount} BONUS</div>
                  {Object.entries(record.reasonI18n).map(([language, reason]) => (
                    <div key={language}>
                      原因 {language} · {reason}
                    </div>
                  ))}
                  <div className="vstack" style={{ gap: 8, marginTop: 8 }}>
                    {record.users.map((user) => (
                      <div key={user.internalUserId}>
                        <div>{user.name}</div>
                        <div className="mono" style={{ color: 'var(--fg-muted)' }}>
                          展示用户 ID · {getReconciliationDisplayUserId(user.displayUserId)}
                        </div>
                        <div className="mono" style={{ color: 'var(--fg-muted)' }}>
                          内部 UUID · {user.internalUserId}
                        </div>
                        {user.error && (
                          <>
                            <div className="mono" style={{ color: 'var(--fg-muted)' }}>
                              HTTP {user.error.httpStatus} · {user.error.code}
                            </div>
                            {user.error.requestId && (
                              <div className="mono" style={{ color: 'var(--fg-muted)' }}>
                                Request ID · {user.error.requestId}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <label
              className="hstack"
              style={{ gap: 7, marginTop: 12, alignItems: 'flex-start', cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={reconciliationAcknowledged}
                onChange={(event) => setReconciliationAcknowledged(event.target.checked)}
              />
              <span>我已记录并将人工对账</span>
            </label>
            <div style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
              勾选后可关闭弹窗；关闭即视为已完成本地记录交接。
            </div>
          </div>
        )}
        <FormRow label="数额" required hint="单次最多发放 2,000 BONUS">
          <div className="vstack" style={{ gap: 8 }}>
            <div className="hstack" style={{ gap: 6 }}>
              <input
                type="number"
                className="input mono"
                style={{ width: 140, fontSize: 13 }}
                value={amount}
                min={1}
                max={2000}
                onChange={(e) =>
                  setAmount(Math.max(0, Math.min(2000, Number(e.target.value) || 0)))
                }
              />
              <span className="mono muted" style={{ fontSize: 11 }}>
                BONUS
              </span>
            </div>
            <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
              {PRESETS.map((p) => (
                <button
                  key={p}
                  className="btn btn-sm"
                  onClick={() => setAmount(p)}
                  style={{
                    fontSize: 11,
                    background: amount === p ? 'var(--accent-soft)' : undefined,
                    color: amount === p ? 'var(--accent)' : undefined,
                    borderColor: amount === p ? 'var(--accent)' : undefined,
                  }}
                >
                  +{p.toLocaleString()}
                </button>
              ))}
            </div>
          </div>
        </FormRow>
        <FormRow label="快捷原因" hint="点击预填中文文案">
          <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
            {REASONS.map((r) => (
              <button
                key={r}
                className="btn btn-sm"
                onClick={() => setReasonI18n((prev) => ({ ...prev, zh: r }))}
                style={{
                  fontSize: 11,
                  background: reasonI18n.zh === r ? 'var(--accent-soft)' : undefined,
                  color: reasonI18n.zh === r ? 'var(--accent)' : undefined,
                  borderColor: reasonI18n.zh === r ? 'var(--accent)' : undefined,
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </FormRow>
        {/* 发放原因给 C 端展示 → reason_i18n 多语言录入（必填 zh/en，en 为契约强校验地板） */}
        <MultiLangFormRows
          label="发放原因"
          value={reasonI18n}
          onChange={setReasonI18n}
          requiredLangs={requiredLangs}
          pinnedLangs={pinnedLangs}
          availableLangs={enabledLangs}
        />
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 4 }}>
          发放原因展示语言由 server 按各用户最近使用语言派生（en 为 fallback 地板）。
        </div>
      </div>
    </Dialog>
  );
}
